<template>
    <application-layout>
        <v-main>
            <v-form
              @submit.prevent="submitFacilityForm"
              ref="editFacilityForm"
              v-if="initialLoadComplete"
            >
                <v-container fluid>
                    <v-row>
                        <v-col>
                            <h1 class="pt-1">Edit {{ currentFacility.name }}</h1>
                        </v-col>
                        <v-spacer />
                        <v-col>
                            <v-switch
                                data-testid="facility-edit-visibility-switch"
                                class="facility-edit-visibility-switch"
                                :label='currentFacility.published ? "Published" : "Not Published"'
                                v-model="currentFacility.published"
                            />
                        </v-col>
                    </v-row>

                    <v-tabs show-arrows>
                        <v-tab
                            data-testid="facility-edit-basics-tab"
                            class="facility-edit-basics-tab"
                        >
                            Basics
                        </v-tab>
                        <v-tab
                            data-testid="facility-edit-location-details-tab"
                            class="facility-edit-location-details-tab"
                        >
                            Location Details
                        </v-tab>
                        <v-tab
                            data-testid="facility-edit-features-tab"
                            class="facility-edit-features-tab"
                        >
                            Features
                        </v-tab>
                        <v-tab
                            data-testid="facility-edit-photos-tab"
                            class="facility-edit-photos-tab"
                        >
                            Photos
                        </v-tab>
                        <v-tab
                            data-testid="facility-edit-website-options-tab"
                            class="facility-edit-website-options-tab"
                            @click="launchFroala"
                        >
                            Website Options
                        </v-tab>
                        <v-tab
                            data-testid="facility-edit-unit-management-tab"
                            class="facility-edit-unit-management-tab"
                            v-if="!currentFacility.pms_enabled"
                        >
                            <span>Unit Management</span>
                        </v-tab>
                        <v-tab
                            data-testid="facility-edit-unit-attributes-tab"
                            class="facility-edit-unit-attributes-tab"
                        >
                            <span>Unit Attributes</span>
                        </v-tab>
                        <v-tab
                            data-testid="facility-edit-reviews-tab"
                            class="facility-edit-reviews-tab"
                        >
                            Reviews
                        </v-tab>
                        <v-tab
                          data-testid="facility-edit-social-media-tab"
                          class="facility-edit-social-media-tab"
                        >
                            Social Media Links
                        </v-tab>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <facility-basics />
                            <facility-hours />
                        </v-tab-item>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <location-details show-map />
                        </v-tab-item>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <facility-features />
                        </v-tab-item>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <facility-photos />
                        </v-tab-item>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <website-options :launchFroala="shouldLaunchFroala" />
                        </v-tab-item>

                        <v-tab-item
                            v-if="!currentFacility.pms_enabled"
                            :style="darkTheme ? darkThemeBgColor : ''"
                        >
                            <unit-manager />
                        </v-tab-item>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <unit-attribute-designer />
                        </v-tab-item>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <facility-reviews />
                        </v-tab-item>

                        <v-tab-item :style="darkTheme ? darkThemeBgColor : ''">
                            <facility-social-media-links />
                        </v-tab-item>
                    </v-tabs>

                    <hr class="mt-5" />

                    <v-row >
                        <v-col cols="12">
                            <v-btn
                                data-testid="facility-edit-cancel-btn"
                                class="facility-edit-cancel-btn mr-3 ml-0"
                                @click="handleCancelEditFacility"
                                color="error"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                data-testid="facility-edit-save-btn"
                                class="facility-edit-cancel-btn"
                                color="primary"
                                type="submit"
                                :loading="loading"
                                :disabled="loading"
                            >
                                Save
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
            <v-container
                class="fill-height"
                fluid
                v-if="!initialLoadComplete"
            >
                <v-row align="center" justify="center">
                    <v-progress-circular indeterminate color="primary" />
                </v-row>
            </v-container>
        </v-main>
    </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import FacilityBasics from "@/views/facility/partials/FacilityBasics.vue";
import FacilityFeatures from "@/views/facility/partials/FacilityFeatures.vue";
import FacilityHours from "@/views/facility/partials/FacilityHours.vue";
import FacilityPhotos from "@/views/facility/partials/FacilityPhotos.vue";
import FacilityReviews from "@/views/facility/partials/FacilityReviews.vue";
import FacilitySocialMediaLinks from "@/views/facility/partials/FacilitySocialMediaLinks";
import LocationDetails from "@/views/facility/partials/FacilityDetails.vue";
import UnitAttributeDesigner from "@/views/facility/partials/UnitAttributeDesigner.vue";
import UnitManager from "@/views/facility/partials/UnitManagement.vue";
import WebsiteOptions from "@/views/facility/partials/WebsiteOptions.vue";

export default {
  data() {
    return {
      initialLoadComplete: false,
      loading: false,
      shouldLaunchFroala: false
    };
  },

  computed: {
    darkThemeBgColor() {
      return this.$store.state.app.darkThemeBgColor;
    },
    currentFacility: {
      get() {
        return this.$store.state.facility.currentFacility;
      },

      set(newValue) {
        this.$store.commit("setCurrentFacilityData", newValue);
      }
    },

    darkTheme() {
      return this.$vuetify.theme.dark;
    }
  },

  methods: {
    async handleCancelEditFacility() {
      await this.$store.commit("resetCurrentFacility");
      this.$router.back();
    },
    async submitFacilityForm() {
      if (this.$refs.editFacilityForm.validate()) {
        this.loading = true;
        await this.$store.dispatch("updateFacility")
          .finally(() => {
            this.loading = false;
          });
      }
    },

    launchFroala() {
      this.shouldLaunchFroala = true;
    }
  },

  components: {
    ApplicationLayout,
    FacilityBasics,
    LocationDetails,
    FacilityHours,
    FacilityFeatures,
    FacilityPhotos,
    FacilityReviews,
    UnitAttributeDesigner,
    UnitManager,
    WebsiteOptions,
    FacilitySocialMediaLinks
  },

  async created() {
    await this.$store.commit("resetCurrentFacility");
    this.$store.dispatch("getFacility", this.$route.params.facilityId)
      .then(() => {
        this.initialLoadComplete = true;
      });
  }
};
</script>
